import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { Stack } from '@fluentui/react';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { Input } from '@bingads-webui-react/primitive-with-validation-and-signal';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  MenuItem,
} from '@bingads-webui-react/primitive';

export const BudgetInput = ({
  budgetTitle,
  classes,
  showDropdown,
  i18n,
  legacyI18n,
  onBudgetInputValidate,
  onBudgetChange,
  validationKey,
  value,
  validationProps,
  RANGE_STEP,
  schema,
  isCurrencyDisabled,
  onSelectCurrency,
  selectedCurrencyText,
  currencies,
  onRenderInlineHelp,
  onRenderCurrencyDropDown,
  inputClassName,
}) => {
  const showCurrencyOnly = useMemo(() => (
    <div className={classNames('display-currency-text', classes.currencyText)}>
      {selectedCurrencyText}
    </div>), [classes, selectedCurrencyText]);

  const displayCurrencySelection = useMemo(
    () => (
      <div className={classes.dropdownContainer}>
        <div className={classes.inline}>
          <Dropdown
            id="curr-dropdown"
            disabled={isCurrencyDisabled}
            onSelect={onSelectCurrency}
          >
            <DropdownToggle>{selectedCurrencyText}</DropdownToggle>
            <DropdownMenu>
              {
                _.map(currencies, itemValue => (
                  <MenuItem className="list-selector-item" key={itemValue.currency} eventKey={itemValue.currency}>
                    {itemValue.currencyLocalizationKey}
                  </MenuItem>
                ))
              }
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
    ),
    [classes.dropdownContainer, classes.inline, isCurrencyDisabled, onSelectCurrency, selectedCurrencyText, currencies]
  );
  return (
    <>
      <div className={classes.budgetTitle}>
        <Stack horizontal>
          {
            // eslint-disable-next-line jsx-a11y/label-has-for
            <label htmlFor="budget" className="budget-title">
              {
                budgetTitle ||
                i18n.getString(_TL_('Set your daily budget for this campaign.'))
              }
            </label>
          }
          {onRenderInlineHelp && onRenderInlineHelp()}
        </Stack>
      </div>
      <div className={classes.budgetDetailsContainer}>
        <div className={classNames(classes.budgetInputContainer, 'budget-input-container')}>
          <ValidationGroup
            key={validationKey}
            validationProps={{
              schema,
              i18n: legacyI18n,
              onValidate: onBudgetInputValidate,
            }}
          >
            <Input
              id="budget"
              name="budget"
              step={RANGE_STEP}
              value={value}
              validationProps={validationProps}
              className={classNames(classes.inputText, inputClassName)}
              onChange={onBudgetChange}
            />
          </ValidationGroup>
        </div>

        {
          // eslint-disable-next-line no-nested-ternary
          showDropdown ? (onRenderCurrencyDropDown ? onRenderCurrencyDropDown({ onSelectCurrency }) : displayCurrencySelection) : showCurrencyOnly
        }
      </div>
    </>
  );
};
BudgetInput.propTypes = {
  showDropdown: PropTypes.bool,
  budgetTitle: PropTypes.string,
  classes: PropTypes.shape({
    budgetDetailsContainer: PropTypes.string,
    budgetInputContainer: PropTypes.string,
    budgetTitle: PropTypes.string,
    currencyText: PropTypes.string,
    dropdownContainer: PropTypes.string,
    inline: PropTypes.string,
    inputText: PropTypes.string,
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  legacyI18n: PropTypes.shape({}).isRequired,
  onBudgetInputValidate: PropTypes.func.isRequired,
  onBudgetChange: PropTypes.func.isRequired,
  validationKey: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  validationProps: PropTypes.shape({}).isRequired,
  RANGE_STEP: PropTypes.number.isRequired,
  schema: PropTypes.shape({}).isRequired,
  isCurrencyDisabled: PropTypes.bool.isRequired,
  onSelectCurrency: PropTypes.func.isRequired,
  selectedCurrencyText: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onRenderInlineHelp: PropTypes.func,
  onRenderCurrencyDropDown: PropTypes.func,
  inputClassName: PropTypes.string,
};

BudgetInput.defaultProps = {
  showDropdown: false,
  budgetTitle: undefined,
  classes: {},
  onRenderInlineHelp: null,
  onRenderCurrencyDropDown: null,
  inputClassName: null,
};
